/**
 * Domain constants (Onada 1).
 *
 * Single source of truth for the regulatory-fixed sets (the 14 EU allergens) and the
 * default thresholds mirrored from tenant_settings defaults in the data model. Used by
 * both the apps and edge functions so that, e.g., merma / weighing warning thresholds
 * never get hardcoded twice.
 */

import {
  type AllergenCode,
  type ArticleType,
  type MovementReason,
  type MovementType,
  type IncidentSeverity,
  type NotificationSeverity,
} from '../enums/index.js';

/**
 * The 14 EU allergens (Reg. UE 1169/2011, Annex II), in regulatory order.
 * `code` matches the `allergen_code` enum; `eu_annex_number` is the official 1..14 index.
 * UI labels are NEVER taken from here — they come from i18n (domain.allergens.*).
 */
export interface AllergenDefinition {
  readonly code: AllergenCode;
  readonly euAnnexNumber: number;
}

export const EU_ALLERGENS: readonly AllergenDefinition[] = [
  { code: 'gluten', euAnnexNumber: 1 },
  { code: 'crustaceans', euAnnexNumber: 2 },
  { code: 'eggs', euAnnexNumber: 3 },
  { code: 'fish', euAnnexNumber: 4 },
  { code: 'peanuts', euAnnexNumber: 5 },
  { code: 'soybeans', euAnnexNumber: 6 },
  { code: 'milk', euAnnexNumber: 7 },
  { code: 'nuts', euAnnexNumber: 8 },
  { code: 'celery', euAnnexNumber: 9 },
  { code: 'mustard', euAnnexNumber: 10 },
  { code: 'sesame', euAnnexNumber: 11 },
  { code: 'sulphites', euAnnexNumber: 12 },
  { code: 'lupin', euAnnexNumber: 13 },
  { code: 'molluscs', euAnnexNumber: 14 },
] as const;

/** Fast lookup / validation helper for the 14 allergen codes. */
export const EU_ALLERGEN_CODES: readonly AllergenCode[] = EU_ALLERGENS.map(
  (a) => a.code,
);

/** Exactly 14 — enforced as a compile-time-checkable invariant for tests. */
export const EU_ALLERGEN_COUNT = 14 as const;

/**
 * Merma (yield-loss) reason codes valid as a `merma_reason` on a work order close.
 * Merma ALWAYS carries a reason (CLAUDE.md invariant). Subset of `movement_reason`
 * that denotes an actual loss (not a recount/correction/normal flow).
 */
export const MERMA_REASON_CODES = [
  'burnt',
  'dropped',
  'expired',
  'damage',
  'loss',
] as const satisfies readonly MovementReason[];
export type MermaReasonCode = (typeof MERMA_REASON_CODES)[number];

/**
 * Movement types that REQUIRE a written reason (mandatory-reason invariant):
 * adjustment, write_off and reversal. reception / o01_consumption /
 * production_output / shipment are derived from documented events and do not
 * require a manual reason.
 */
export const MOVEMENT_TYPES_REQUIRING_REASON = [
  'adjustment',
  'write_off',
  'reversal',
] as const satisfies readonly MovementType[];

/** Type guard: does this movement type require a written reason? */
export function movementTypeRequiresReason(type: MovementType): boolean {
  return (MOVEMENT_TYPES_REQUIRING_REASON as readonly MovementType[]).includes(type);
}

/**
 * Severity ladders, ordered ascending. Useful for comparisons / escalation
 * (e.g. "high incident triggers a critical notification").
 */
export const INCIDENT_SEVERITY_ORDER = [
  'low',
  'medium',
  'high',
] as const satisfies readonly IncidentSeverity[];

export const NOTIFICATION_SEVERITY_ORDER = [
  'info',
  'medium',
  'critical',
] as const satisfies readonly NotificationSeverity[];

/**
 * Default per-tenant thresholds, mirrored from tenant_settings column defaults in the
 * data model. A tenant may override any of these; these are the fallback values.
 */
export const DEFAULT_THRESHOLDS = {
  /** Informative warning when OT merma exceeds this %. Never blocks. */
  mermaThresholdPct: 5.0,
  /** Visual warning when a weighing deviates more than this %. Never blocks. */
  weighingDeviationThresholdPct: 5.0,
  /** Days before expiry at which to raise an expiry alert. */
  expiryAlertLeadDays: 3,
  /** Operator session length (one shift) in hours. */
  operatorSessionHours: 8,
  /** Contextual alert when ingredient cost rises above this % (per article default). */
  costAlertThresholdPct: 10.0,
} as const;

/**
 * Default auto-numbering formats (lot number is NEVER entered manually — generated on
 * OT close; OT number also auto-generated). Mirrors tenant_settings defaults.
 * `{seq:0N}` = zero-padded sequence; see domain/lot.ts for the format renderer.
 */
export const DEFAULT_NUMBERING_FORMATS = {
  lotNumberingFormat: 'L-{seq:05}',
  workOrderNumberingFormat: 'OT-{seq:05}',
  /** Internal supplier lot created at MP reception (start of traceability). */
  internalLotNumberingFormat: 'MP-{seq:05}',
  /** Purchase order (comanda de compra) reference number. */
  purchaseOrderNumberingFormat: 'OC-{seq:05}',
  /** Reception note (albarà de recepció) — distinct sequence from the MP- lot. */
  receptionNumberingFormat: 'AR-{seq:05}',
  /** Sales order (comanda de venda) reference number. */
  salesOrderNumberingFormat: 'CV-{seq:05}',
  /** Shipment / albarà de venda — its own sequence, generated at expedition. */
  shipmentNumberingFormat: 'AV-{seq:05}',
  /** Purchase return note (albarà de devolució) — generated at the physical exit (OBR-133). */
  purchaseReturnNumberingFormat: 'DV-{seq:05}',
} as const;

/** Decimal precision conventions matching the DB numeric types. */
export const PRECISION = {
  /** numeric(14,3) — quantities. */
  quantityDecimals: 3,
  /** numeric(6,2) — percentages. */
  percentDecimals: 2,
  /** numeric(14,4) — money. */
  moneyDecimals: 4,
} as const;

/**
 * CONVERSIÓ VARIABLE — preparada, amagada a l'MVP.
 *
 * Un article de conversió variable és el que no té una equivalència fixa entre
 * la unitat en què es compta i la unitat en què es mesura: una peça tallada que
 * es pesa a cada moviment. El concepte és el que abans es deia «pes variable»,
 * i el nom nou és el que descriu què passa de debò — el que varia és la
 * conversió, no el pes de la cosa.
 *
 * Quan s'activi tocarà RECEPCIÓ (la quantitat rebuda no es dedueix de les
 * unitats), EXPEDICIÓ (el que s'envia es pesa) i EXISTÈNCIES (el saldo es porta
 * en la unitat mesurada). Res d'això existeix encara, i La Nevateria no en té
 * cap cas: oferir la casella a l'MVP només seria una pregunta sense resposta a
 * la fitxa d'article.
 *
 * Amagat, no esborrat: la columna `articles.is_variable_weight` hi segueix amb
 * el que hi hagi desat, l'esquema l'accepta i posar això a `true` torna a
 * ensenyar el control a la fitxa i la columna a l'importador. La columna manté
 * el nom antic a la base de dades; el canvi de nom de columna, si es fa, va a
 * part d'aquesta decisió de producte.
 */
export const VARIABLE_CONVERSION_ENABLED = false;

/**
 * Abreviatures de la tipologia d'article que accepta l'importador, a més del nom
 * sencer i del codi intern (Linear OBR-9, JM 2026-09-27).
 *
 * Són les del sector —MP és matèria primera al vocabulari d'Obrava— i les
 * MATEIXES en tots els idiomes, com els marcadors de l'assumpte d'una comanda:
 * coincideixen en català i en castellà, i un full que passa d'una persona a una
 * altra no canvia de significat pel camí. La plantilla continua escrivint el nom
 * sencer; l'abreviatura és una drecera per a qui l'omple a mà.
 */
export const ARTICLE_TYPE_ABBREVIATIONS: Readonly<Record<ArticleType, string>> = {
  raw_material: 'MP',
  intermediate: 'SE',
  finished: 'PA',
  packaging: 'AUX',
};
